export interface Chain {
  label?: string
  steps: string
}

export interface Volume {
  icon: string
  number: string
  title: string
  subtitle: string
  problem: string[]
  chains?: Chain[]
  afterChain?: string[]
  bulletsIntro: string
  bullets: string[]
  extraList?: { label: string; items: string[] }
  visualFocus: string[]
  result: string[]
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    icon: '📘',
    number: 'System 1 — Volume 1',
    title: 'Olympic Weightlifting Foundations & Coaching Essentials',
    subtitle: 'Build the visual and technical foundation behind every successful Weightlifting session',
    problem: [
      'Good Olympic Weightlifting coaching starts long before heavy Snatches and Clean & Jerks.',
      'This system gives coaches the foundations needed to organize positions, athlete preparation and technical instruction before progressing into more demanding movements.',
      'Instead of trying to explain every concept from memory, you have clear visual references that make fundamental positions easier to show, understand and coach.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Visual grip and stance references',
      'Starting-position guidance',
      'Athlete setup and body-position checkpoints',
      'Barbell and equipment fundamentals',
      'Weightlifting terminology',
      'Warm-up structure',
      'Movement preparation',
      'Fundamental mobility considerations',
      'Coaching observation',
      'Technical checkpoints',
      'Coaching cues',
      'Common beginner errors',
    ],
    visualFocus: ['Position Maps', 'Coaching Cues', 'Setup References', 'Checklists', 'Correct vs Incorrect'],
    result: [
      'Your athletes develop stronger foundations and you teach them through a repeatable visual coaching methodology instead of improvising the basics every session.',
    ],
  },
  {
    icon: '🏋️',
    number: 'System 2 — Volume 2',
    title: 'Snatch Technique, Progressions & Error Correction',
    subtitle: 'A complete visual coaching system for teaching the Snatch step by step',
    problem: [
      'The Snatch is one of the most technical movements in strength sports.',
      'Telling an athlete “keep the bar closer” or “finish the pull” is not always enough.',
      'This visual system allows you to show the movement, identify the phase where the lift breaks down and connect the technical error to an appropriate correction.',
    ],
    chains: [
      {
        label: 'The Snatch is broken down visually into:',
        steps: 'Start → First Pull → Transition → Extension → Turnover → Receive → Recover',
      },
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Starting-position diagrams',
      'Snatch grip visuals',
      'First-pull sequences',
      'Knee-transition diagrams',
      'Power-position breakdowns',
      'Bar-path graphics',
      'Extension mechanics',
      'Turnover sequences',
      'Receiving-position maps',
      'Overhead stability',
      'Footwork diagrams',
      'Correct vs Incorrect comparisons',
      'Snatch progressions',
      'Technical drills',
      'Common errors and corrections',
      'Beginner-to-intermediate progression ladders',
    ],
    visualFocus: [
      'Bar Paths',
      'Movement Sequences',
      'Arrows',
      'Position Maps',
      'Wrong vs Correct',
      'Drill Progressions',
    ],
    result: [
      'You stop looking at the Snatch as one complicated movement.',
      'You can see the phase, identify the error, show the correction and select the drill.',
    ],
  },
  {
    icon: '🏋️',
    number: 'System 3 — Volume 3',
    title: 'Clean & Jerk Technique, Progressions & Error Correction',
    subtitle: 'Turn the Clean & Jerk into a clear visual coaching progression',
    problem: [
      'The Clean & Jerk combines multiple technical phases.',
      'Without a clear progression, athletes may understand individual cues but still struggle to connect the complete movement.',
      'This system visually breaks the Clean and Jerk into smaller coaching decisions.',
    ],
    chains: [
      {
        label: 'Clean:',
        steps: 'Setup → First Pull → Transition → Extension → Turnover → Receive → Recover',
      },
      { label: 'Jerk:', steps: 'Stance → Dip → Drive → Move Under → Receive → Recover' },
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Clean starting position',
      'First pull',
      'Knee transition',
      'Clean extension',
      'Turnover',
      'Front-rack position',
      'Receiving position',
      'Clean recovery',
      'Jerk stance',
      'Dip mechanics',
      'Drive direction',
      'Split Jerk footwork',
      'Overhead receiving position',
      'Jerk recovery',
      'Timing',
      'Common errors',
      'Technical correction drills',
      'Visual progressions',
    ],
    visualFocus: [
      'Step-by-Step Sequences',
      'Footwork Maps',
      'Bar Position',
      'Clean vs Jerk Phases',
      'Error Corrections',
    ],
    result: [
      'Instead of trying to teach the Clean & Jerk as one complicated lift, you visually isolate each phase and build the movement progressively.',
    ],
  },
  {
    icon: '💪',
    number: 'System 4 — Volume 4',
    title: 'Strength, Mobility & Weightlifting-Specific Development',
    subtitle: 'Build the strength and positions that support better Olympic Weightlifting',
    problem: [
      "Technique cannot be separated from the athlete's ability to control the positions required by the lifts.",
      'This system connects strength, mobility and positional development directly to Olympic Weightlifting.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Front Squat development',
      'Back Squat development',
      'Pull variations',
      'Pressing work',
      'Overhead strength',
      'Positional strength',
      'Posterior-chain development',
      'Trunk stability',
      'Front-rack preparation',
      'Overhead preparation',
      'Ankle mobility',
      'Hip mobility',
      'Shoulder mobility',
      'Weightlifting-specific accessory exercises',
    ],
    visualFocus: [
      'Exercise Positions',
      'Mobility Sequences',
      'Movement Checkpoints',
      'Progression Cards',
      'Quick References',
    ],
    result: [
      'Strength and mobility stop being random accessory work.',
      'You can visually connect each exercise to the positions and physical qualities your athletes actually need.',
    ],
  },
  {
    icon: '📊',
    number: 'System 5 — Volume 5',
    title: 'Athlete Assessment, Programming & Performance Development',
    subtitle: 'Turn individual sessions into a complete athlete-development system',
    problem: [
      'A good session matters.',
      'But long-term athlete development requires more than choosing exercises for today.',
      'You need to know where the athlete is now, what needs improvement and what should happen next.',
    ],
    chains: [
      {
        label: 'This visual system helps you move through:',
        steps: 'Assess → Identify → Plan → Train → Track → Review → Adjust',
      },
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Athlete assessment',
      'Technical evaluation',
      'Snatch assessment',
      'Clean & Jerk assessment',
      'Strength markers',
      'Mobility observations',
      'Training priorities',
      'Exercise selection',
      'Training frequency',
      'Volume and intensity considerations',
      'Beginner programming',
      'Intermediate programming',
      'Progress tracking',
      'Performance reviews',
      'Technical-development planning',
    ],
    visualFocus: [
      'Assessment Sheets',
      'Progress Maps',
      'Checklists',
      'Development Pathways',
      'Tracking Tools',
    ],
    result: [
      'You stop managing isolated training sessions and begin following a structured athlete-development pathway that you can actually see, track and adjust.',
    ],
  },
  {
    icon: '📚',
    number: 'System 6 — Volume 6',
    title: '150 Ready-to-Coach Olympic Weightlifting Sessions',
    subtitle: 'Stop Building Every Training Session From Scratch',
    problem: [
      'This is the practical heart of the 6-in-1 system.',
      'You receive 150 complete Olympic Weightlifting coaching sessions ready to use, organized across the major areas of athlete development.',
    ],
    chains: [
      {
        label: 'Instead of starting from an empty page, choose a session according to:',
        steps: 'Athlete Level · Technical Objective · Skill Focus · Available Time · Training Priority',
      },
    ],
    afterChain: ['Then open the session and coach.'],
    bulletsIntro: 'Sessions cover:',
    bullets: [
      'Foundations & beginner development',
      'Tools and barbell control',
      'Weightlifting preparation',
      'Snatch fundamentals',
      'Snatch development',
      'Clean fundamentals',
      'Clean development',
      'Jerk technique',
      'Clean & Jerk integration',
      'Technical correction',
      'Strength development',
      'Mobility work',
      'Athlete assessment',
      'Programming applications',
      'Advanced integration',
    ],
    extraList: {
      label: 'Each session can include:',
      items: [
        'Objective',
        'Duration',
        'Tools / Equipment',
        'Warm-Up',
        'Technical Focus',
        'Visual Position Reference',
        'Coach Demonstration',
        'Athlete Practice',
        'Main Lift',
        'Accessory Work',
        'Coaching Cues',
        'Common Error',
        'Correction',
        'Quick Assessment',
        'Level Up',
      ],
    },
    visualFocus: [
      'Ready-to-Use Structures',
      'Coaching Cards',
      'Technique References',
      'Quick Checks',
      'Progression Systems',
    ],
    result: [
      'Instead of wondering what to coach tomorrow, you open the visual session library and begin with a complete structure.',
    ],
    popular: true,
  },
]
